import {
  AbilityBuilder,
  ConditionsMatcher,
  MatchConditions,
  PureAbility,
} from '@casl/ability';

import { PERMISSION_OPERATIONS } from '@/shared/features/ability/hooks/use-user-ability/use-user-ability.constants';
import {
  TAction,
  TAggregationType,
  TAppAbility,
  TCondition,
  TEntityActionType,
} from '@/shared/features/ability/hooks/use-user-ability/use-user-ability.types';
import { createGroupAncestryChecker } from '@/shared/features/ability/utils/group-ancestry-checker';
import { useReadAllResourceGroupsWithoutPermissionChecksQuery } from '@/shared/redux/api/assets/groups/groups-api';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import { useReadPoliciesForOwnUserQuery } from '@/shared/redux/api/ident/ident-api';
import { useAppSelector } from '@/shared/redux/hooks/hooks';
import { PolicyFragment } from '@/shared/types/types.generated';
import { capitalizeString } from '@/shared/utils/format';

const lambdaMatcher: ConditionsMatcher<MatchConditions> = (matchConditions) =>
  matchConditions;

export const useUserAbility = () => {
  const user = useAppSelector(selectUser);
  const { can, build } = new AbilityBuilder<TAppAbility>(PureAbility);
  const { data, isLoading: isPoliciesLoading } = useReadPoliciesForOwnUserQuery(
    {},
    { skip: !user },
  );
  const { data: groupsData, isLoading: isGroupsLoading } =
    useReadAllResourceGroupsWithoutPermissionChecksQuery({}, { skip: !user });
  const groups = groupsData?.readAllResourceGroupsForPermissions;
  const checker = createGroupAncestryChecker(groups);

  const checkPermissionByType = (
    id: string,
    aggregationType: TAggregationType,
    assetId?: string,
    groupId?: string,
    tags?: string[],
  ) => {
    if (aggregationType === 'tag' && tags) {
      return tags?.includes(id);
    }

    if (aggregationType === 'group') {
      return (
        id === assetId ||
        id === groupId ||
        Boolean(checker?.isChildOf(id, groupId))
      );
    }

    return id === assetId;
  };

  const capitalizeEntity = (entity: string): TEntityActionType => {
    if (entity?.toLowerCase() === 'resourcegroup') {
      return 'ResourceGroup';
    }

    if (entity?.toLowerCase() === 'auditlog') {
      return 'AuditLog';
    }

    if (entity?.toLowerCase() === 'userrole') {
      return 'UserRole';
    }

    return capitalizeString(entity) as TEntityActionType;
  };

  const parseBackendAction = (
    backendAction: string,
  ): { action: TAction; entity: TEntityActionType } | null => {
    const [entity, actionName] = backendAction.split(':');

    if (!actionName) {
      return null;
    }

    const action = PERMISSION_OPERATIONS.filter((op) =>
      actionName.startsWith(op),
    ).sort((a, b) => b.length - a.length)[0];

    if (!action) {
      return null;
    }

    const _entity = capitalizeEntity(entity);

    return { action, entity: _entity };
  };

  const createRulesFromPolicy = (policy: PolicyFragment) => {
    policy.action.forEach((backendAction) => {
      // Admin Role - full wildcard
      if (backendAction === '*') {
        can('manage', 'all');
        return;
      }

      // Entity wildcard (e.g., 'gateway:*') - grants all actions on the entity
      if (backendAction.endsWith(':*')) {
        const [entity] = backendAction.split(':');
        const capitalizedEntity = capitalizeEntity(entity);
        can('manage', capitalizedEntity);
        return;
      }

      const parsed = parseBackendAction(backendAction);

      if (!parsed) {
        return;
      }

      const { action, entity } = parsed;

      policy.resource.forEach((resource) => {
        if (resource.includes('*') || action === 'read') {
          can(action, entity);
        }

        const _resource = resource.split(':');

        if (_resource.length < 3) {
          return;
        }

        const aggregationType = _resource[1] as TAggregationType;
        const resourceId = _resource[2];

        can(action, entity, ({ tags, attribute }: TCondition) =>
          checkPermissionByType(
            resourceId,
            aggregationType,
            attribute?.id,
            attribute?.groupId,
            tags,
          ),
        );
      });
    });
  };

  const allPolicies = data?.readUserRolesForOwnUser?.items?.flatMap(
    ({ policies }) => policies,
  );

  allPolicies?.forEach((policy) => createRulesFromPolicy(policy));

  return {
    ability: build({ conditionsMatcher: lambdaMatcher }),
    isLoading: Boolean(isPoliciesLoading || isGroupsLoading),
  };
};
